/**
 * Available Learning Programs (SW-46 / SNW-64) — the programs LINKED to a school
 * or network that have no request behind them.
 *
 * The list was built from program requests alone, so a program the partner (or
 * the network) linked straight from My Schools never appeared on it, and the
 * counters ignored it. Requesting a program is one way to get one; what the
 * page lists is the programs the account has (Usman, 6 Oct 2026).
 *
 * A linked program is one of the slide's two "Active" statuses, by the same
 * rule a delivered request follows:
 *   Live    linked to the account, not yet linked onward        → Link Programs
 *   Linked  linked onward (a network to one of its schools,
 *           a school to a class or the whole school)            → View Dashboard
 *
 * A program that DOES have a request is already a row — it is left to that row.
 */

const LIVE = 'Live';
const LINKED = 'Linked';

const fmtDate = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return '—';
    // Fixed to 'en-US', like the request rows: the browser's locale is not used.
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};

/**
 * @param {object[]} modules        OrgModules rows (org/modules, module_type learning_program)
 * @param {string[]} requestCodes   program_code of every request the account has
 * @returns {object[]} rows shaped like the request rows of the list
 */
export const linkedOnlyProgramRows = (modules, requestCodes = []) => {
    const taken = new Set((requestCodes || []).filter(Boolean).map(String));
    const seen = new Set();
    return (Array.isArray(modules) ? modules : [])
        .filter((m) => {
            const code = String(m?.module_code || m?.code || '');
            if (!code || taken.has(code) || seen.has(code)) return false;
            seen.add(code);
            return true;
        })
        .map((m) => {
            const code = String(m.module_code || m.code);
            return {
                id:       `linked-${code}`,
                received: fmtDate(m.createdAt),
                isNew:    false,
                program:  m.module_name || m.name || '—',
                cosArea:  '—',
                group:    'Whole school',
                learners: '—',
                duration: '—',
                // linked_onward comes from the API (a network -> a school, a school -> a
                // class); visibility_scope is the older signal and still counts.
                status:   (m.linked_onward === true || !!m.visibility_scope) ? LINKED : LIVE,
                // What the row's buttons read: View Dashboard opens the program by its code.
                raw:      { program_code: code, linked_only: true },
            };
        });
};

/**
 * One page of the list: the requests (paged by the server) first, then the
 * linked programs, which fill what is left of the last request page and the
 * pages after it.
 *
 * @param {object}   opts
 * @param {object[]} opts.requestRows   the request rows the server returned for THIS page
 * @param {number}   opts.requestTotal  how many requests there are in all
 * @param {object[]} opts.linkedRows    every linked-only row (linkedOnlyProgramRows)
 * @param {number}   opts.page          0-based
 * @param {number}   opts.rowsPerPage
 * @returns {{ rows: object[], total: number }}
 */
export const mergeAvailablePage = ({ requestRows = [], requestTotal = 0, linkedRows = [], page = 0, rowsPerPage = 10 }) => {
    const requests = Array.isArray(requestRows) ? requestRows : [];
    const linked = Array.isArray(linkedRows) ? linkedRows : [];
    const total = (Number(requestTotal) || 0) + linked.length;
    const room = Math.max(0, rowsPerPage - requests.length);
    if (!room || !linked.length) return { rows: requests, total };
    // Where this page starts among the linked rows, once the requests are used up.
    const offset = Math.max(0, page * rowsPerPage - (Number(requestTotal) || 0));
    return { rows: [...requests, ...linked.slice(offset, offset + room)], total };
};
